import { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { handleSimpleFormNav } from "@/lib/keyboardNav";
import { Users, Plus, Pencil, Power } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { PaginationBar, usePagination } from "@/components/Pagination";

const emptyU = { name: "", email: "", phone: "", role: "receptionist", org_id: "", branch_id: "", assigned_store: "", password: "" };

export default function UsersMaster() {
  const { user: me } = useAuth();
  const [list, setList] = useState([]);
  const pg = usePagination(list);
  const [roles, setRoles] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [stores, setStores] = useState([]);
  const [f, setF] = useState(emptyU);
  const [editId, setEditId] = useState(null);
  const [open, setOpen] = useState(false);

  const load = () => api.get("/users").then(r => setList(r.data)).catch(e => toast.error(formatErr(e)));
  useEffect(() => {
    load();
    api.get("/roles").then(r => setRoles(r.data)).catch(() => {});
    api.get("/organisation").then(r => setOrgs(Array.isArray(r.data) ? r.data : (r.data ? [r.data] : []))).catch(() => {});
    api.get("/stores").then(r => setStores(r.data.filter(s => s.status === "Active"))).catch(() => {});
  }, []);

  const branchesFor = (oid) => (orgs.find(o => o.id === oid)?.branches) || [];
  const orgName = (oid) => orgs.find(o => o.id === oid)?.business_name || "-";

  const save = async () => {
    try {
      if (editId) {
        const payload = { name: f.name, phone: f.phone || null, role: f.role,
          org_id: f.org_id || null, branch_id: f.branch_id || null, assigned_store: f.assigned_store || "" };
        if (f.password) payload.password = f.password;
        await api.patch(`/users/${editId}`, payload);
      } else {
        if (!f.email || !f.password) { toast.error("Email & password required"); return; }
        await api.post("/users", { email: f.email, password: f.password,
          name: f.name, role: f.role, phone: f.phone || null,
          org_id: f.org_id || null, branch_id: f.branch_id || null, assigned_store: f.assigned_store || null });
      }
      toast.success(editId ? "Updated" : "User added");
      setF(emptyU); setEditId(null); setOpen(false); load();
    } catch (e) { toast.error(formatErr(e)); }
  };
  const edit = (u) => {
    setF({ name: u.name, email: u.email, phone: u.phone || "", role: u.role,
      org_id: u.org_id || "", branch_id: u.branch_id || "", assigned_store: u.assigned_store || "", password: "" });
    setEditId(u.id); setOpen(true);
  };
  const toggleActive = async (u) => {
    if (me.id === u.id) { toast.error("Cannot deactivate yourself"); return; }
    try {
      await api.patch(`/users/${u.id}`, { is_active: !(u.is_active !== false) });
      toast.success(u.is_active === false ? "Activated" : "Deactivated"); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div className="p-8">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="text-primary text-xs uppercase tracking-[0.3em] mb-2 flex items-center gap-2"><Users className="w-3 h-3"/> Owner only · Staff & access</div>
          <h1 className="font-display font-black text-4xl uppercase tracking-tighter">User Master</h1>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setF(emptyU); setEditId(null); } }}>
          <DialogTrigger asChild>
            <Button data-testid="add-user-btn" className="rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em] text-xs"><Plus className="w-4 h-4 mr-1"/> New User</Button>
          </DialogTrigger>
          <DialogContent className="rounded-sm bg-card border-border max-w-lg">
            <DialogHeader><DialogTitle className="font-display uppercase">{editId ? "Edit" : "New"} User</DialogTitle></DialogHeader>
            <div className="space-y-3" onKeyDownCapture={handleSimpleFormNav}>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Name</Label><Input data-testid="u-name" value={f.name} onChange={(e) => setF(x => ({ ...x, name: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border"/></div>
                <div><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Phone</Label><Input data-testid="u-phone" value={f.phone} onChange={(e) => setF(x => ({ ...x, phone: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border"/></div>
              </div>
              <div><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Email</Label><Input data-testid="u-email" type="email" value={f.email} disabled={!!editId} onChange={(e) => setF(x => ({ ...x, email: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border"/></div>
              <div><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Password {editId && <span className="text-muted-foreground normal-case tracking-normal">(leave blank to keep)</span>}</Label><Input data-testid="u-password" type="password" value={f.password} onChange={(e) => setF(x => ({ ...x, password: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border"/></div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Role</Label>
                  <Select value={f.role} onValueChange={(v) => setF(x => ({ ...x, role: v }))}>
                    <SelectTrigger data-testid="u-role" className="mt-1 rounded-sm bg-secondary border-border"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="super_admin">SAAS ADMIN</SelectItem>
                      {roles.filter(r => r.name !== "super_admin").map(r => <SelectItem key={r.id} value={r.name}>{r.name.toUpperCase()}{r.is_system ? "" : " (custom)"}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Organisation</Label>
                  <Select value={f.org_id || "none"} onValueChange={(v) => setF(x => ({ ...x, org_id: v === "none" ? "" : v, branch_id: "" }))}>
                    <SelectTrigger data-testid="u-org" className="mt-1 rounded-sm bg-secondary border-border"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— No organisation —</SelectItem>
                      {orgs.map(o => <SelectItem key={o.id} value={o.id}>{o.business_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Branch {f.org_id ? "" : "(pick an organisation first)"}</Label>
                <Select value={f.branch_id || "none"} onValueChange={(v) => setF(x => ({ ...x, branch_id: v === "none" ? "" : v }))} disabled={!f.org_id}>
                  <SelectTrigger data-testid="u-branch" className="mt-1 rounded-sm bg-secondary border-border"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— All branches —</SelectItem>
                    {branchesFor(f.org_id).map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Assigned Store</Label>
                <Select value={f.assigned_store || "none"} onValueChange={(v) => setF(x => ({ ...x, assigned_store: v === "none" ? "" : v }))}>
                  <SelectTrigger data-testid="u-store" className="mt-1 rounded-sm bg-secondary border-border"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— No default store —</SelectItem>
                    {stores.map(s => <SelectItem key={s.id} value={s.id}>{s.name} · {s.store_type}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Button data-testid="save-user-btn" onClick={save} disabled={!f.name} className="w-full rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em]">{editId ? "Update" : "Create User"}</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="border border-border bg-card rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            <tr>
              <th className="text-left p-3">Name</th><th className="text-left p-3">Email</th>
              <th className="text-left p-3">Phone</th><th className="text-left p-3">Role</th>
              <th className="text-left p-3">Organisation</th><th className="text-left p-3">Branch</th>
              <th className="text-left p-3">Status</th>
              <th className="text-right p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {pg.pageItems.map(u => {
              const branch = orgs.flatMap(o => o.branches || []).find(b => b.id === u.branch_id);
              const active = u.is_active !== false;
              return (
                <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-t border-border">
                  <td className="p-3">{u.name}{me.id === u.id && <span className="text-[10px] text-primary ml-2 uppercase tracking-wider">You</span>}</td>
                  <td className="p-3 font-mono text-xs">{u.email}</td>
                  <td className="p-3 font-mono text-xs text-muted-foreground">{u.phone || "-"}</td>
                  <td className="p-3 text-xs uppercase tracking-wider">{u.role === "super_admin" ? "SaaS Admin" : u.role}</td>
                  <td className="p-3 text-xs">{u.org_id ? orgName(u.org_id) : "-"}</td>
                  <td className="p-3 text-xs">{branch?.name || "-"}</td>
                  <td className="p-3"><span className={`px-2 py-1 text-[10px] uppercase tracking-wider rounded-sm border ${active ? "border-green-500/40 bg-green-500/10 text-green-500" : "border-destructive/40 bg-destructive/10 text-destructive"}`}>{active ? "Active" : "Inactive"}</span></td>
                  <td className="p-3 text-right">
                    <Button data-testid={`edit-user-${u.id}`} size="sm" variant="ghost" onClick={() => edit(u)} className="h-7 w-7 p-0"><Pencil className="w-3 h-3"/></Button>
                    <Button data-testid={`toggle-user-${u.id}`} size="sm" variant="ghost" onClick={() => toggleActive(u)} disabled={me.id === u.id} className={`h-7 w-7 p-0 ${active ? "text-destructive" : "text-green-500"}`}><Power className="w-3 h-3"/></Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <PaginationBar pg={pg}/>
      </div>
    </div>
  );
}
